import * as React from 'react';
import { t } from '@lingui/core/macro';
import { classNames } from 'utils/ui/css';

type VoipStandbyTakeoverProps = {
  /**
   * Promotes this standby tab to the VOIP master (acquire camera, claim the slot, rebuild).
   * Returns a promise so the CTA can hold its pending state until the takeover settles.
   */
  onTakeover: () => void | Promise<void>;
};

/**
 * AVS-4302: the standby self-tile affordance shown when `VOIP_CONFIG.loopbackVideo` is off.
 * Renders over a dark backdrop (a sibling Crossfade layer) — a centered green "Takeover" CTA
 * with a hover/focus tooltip, plus an assistive-tech-only caption.
 *
 * Takeover is a multi-second rebuild (camera grab → claim → destroy/re-init) during which this
 * overlay stays mounted for the first ~second. Without instant feedback the dealer re-presses,
 * thinking the click missed — and each press kicks off ANOTHER full takeover. So the first press
 * latches a pending state: the CTA disables (swallowing repeats) and swaps its label for a spinner.
 */
const VoipStandbyTakeover: React.FC<VoipStandbyTakeoverProps> = ({ onTakeover }) => {
  const [pending, setPending] = React.useState(false);

  const handleTakeover = async (): Promise<void> => {
    if (pending) return;
    setPending(true);
    try {
      await onTakeover();
    } finally {
      // Success promotes this tab to master and unmounts this overlay, so this only runs on a
      // FAILED promotion (e.g. camera denied) where the standby stays put — re-enable the CTA.
      setPending(false);
    }
  };

  return (
    <div className="ex3d-voip-standby-takeover">
      {/* AT-readable explanation (visually hidden via the shared caption class). */}
      <span className="ex3d-voip-takeover-caption">{t`Presenting on another tab`}</span>
      <button
        type="button"
        className={classNames('ex3d-voip-takeover-cta', pending && 'cursor-wait')}
        aria-label={t`Takeover`}
        aria-busy={pending || undefined}
        disabled={pending}
        onClick={() => void handleTakeover()}
      >
        {pending ? (
          <span aria-hidden="true" className="size-4 animate-spin rounded-full border-b-2 border-white" />
        ) : (
          <>
            {t`Takeover`}
            <span className="ex3d-voip-takeover-cta-tooltip" aria-hidden="true">{t`Presenting on another tab`}</span>
          </>
        )}
      </button>
    </div>
  );
};

export default VoipStandbyTakeover;
